import { ReactElement, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ReactComponent as CloseIcon } from "components/Icons/close.svg";
import { AperitifPreset, AperitifPresetBody, AperitifPresetValues, aperitifBodyToForm, emptyAperitifPreset } from "lib/api/aperitifMenu";
import { LANGUAGES, Language } from "features/i18n/translations";
import { assoc } from "ramda";
import { PrimaryButton } from "components/UIComponents/PrimaryButton";
import { AperitifPresetForm } from "./AperitifForm";

interface AperitifPresetSidebarProps {
    preset?: AperitifPreset;
    onClose: () => void;
    onSave: (body: AperitifPresetBody) => void;
    loading?: boolean;
    error?: string | null;
}

export function AperitifPresetSidebar({ preset, onClose, onSave, loading, error }: AperitifPresetSidebarProps): ReactElement {
    const [activeLang, setActiveLang] = useState<Language>("de");
    const [form, setForm] = useState<AperitifPresetBody>(() => (preset ? {...preset.body} : emptyAperitifPreset()));

    function updateForm<K extends keyof AperitifPresetBody>(field: K, value: AperitifPresetBody[K]) {
        setForm(f => assoc<AperitifPresetBody[K], AperitifPresetBody, K>(field, value, f) as unknown as AperitifPresetBody);
    }

    function onChange<T extends keyof AperitifPresetValues>(field: T, value: AperitifPresetValues[T]) {
        if (field === "name") {
            updateForm("name", assoc(activeLang, value, form.name));
        } else if (field === "description") {
            updateForm("description", assoc(activeLang, value, form.description));
        } else {
            updateForm(field, value as any);
        }
    }

    const canSave = (form.name["de"] ?? "").trim().length > 0;

    const handleSave = () => {
        onSave({...form});
    };

    const values: AperitifPresetValues = useMemo(
        () => aperitifBodyToForm(form, activeLang),
        [form, activeLang]
    );


    return (
        <>
            <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
            <div className="fixed inset-y-0 right-0 w-[480px] bg-[#F5F3F0] shadow-xl flex flex-col z-50">
                <div className="flex items-center justify-between p-6">
                    <h2 className="text-xl font-medium">
                        <FormattedMessage id={preset ? "aperitif.sidebar.titleEdit" : "aperitif.sidebar.titleNew"} />
                    </h2>
                    <button className="p-1 hover:bg-warm-gray rounded-lg transition-colors" onClick={onClose}>
                        <CloseIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex gap-1">
                    {LANGUAGES.map(lang => (
                        <button
                            key={lang}
                            className={`px-6 py-3 text-sm font-medium transition-colors relative`}
                            onClick={() => setActiveLang(lang)}
                        >
                            {lang.toUpperCase()}
                            {activeLang === lang && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                            )}
                        </button>
                    ))}
                </div>

                <div className="flex-1 overflow-auto p-6">
                    <AperitifPresetForm values={values} onChange={onChange} />
                </div>

                <div className="p-6 flex flex-col gap-2">
                    {error && (
                        <div className="text-sm text-primary">{error}</div>
                    )}
                    <PrimaryButton
                        disabled={!canSave || loading}
                        onClick={handleSave}
                    >
                        <FormattedMessage id="aperitif.form.save" />
                    </PrimaryButton>
                </div>
            </div>
        </>
    );
}
